import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send, 
  MessageSquare,
  User,
  CheckCircle
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'react-hot-toast';
import { trackFormSubmission } from '../lib/analytics';
import { addDocument } from '../lib/firestore-helpers';
import { notifyRequest } from '../lib/notify';
import FreeMap from '../components/Maps/FreeMap';

const schema = yup.object().shape({
  name: yup.string().required(),
  email: yup.string().email().required(),
  phone: yup.string(),
  subject: yup.string().required(),
  message: yup.string().required().min(10)
});

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const ContactPage: React.FC = () => {
  const { t, translations } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: yupResolver(schema)
  });

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.contactInfo.address'),
      details: [
        t('common.company.address'),
        `${t('common.company.postalCode')} ${t('common.company.city')}`,
        t('common.company.country')
      ],
      link: 'https://goo.gl/maps/your-location',
      linkText: t('contact.contactInfo.viewOnMap')
    },
    {
      icon: Phone,
      title: t('contact.contactInfo.phone'),
      details: [t('common.company.phone')],
      link: `tel:${t('common.company.phone')}`,
      linkText: t('contact.contactInfo.callNow')
    },
    {
      icon: Mail,
      title: t('contact.contactInfo.email'),
      details: [t('common.company.email')],
      link: `mailto:${t('common.company.email')}`,
      linkText: t('contact.contactInfo.sendEmail')
    },
    {
      icon: Clock,
      title: t('common.openingHours.title'),
      details: [
        t('common.openingHours.weekdays'),
        t('common.openingHours.saturday'),
        t('common.openingHours.sunday')
      ]
    }
  ];

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    // Track form submission attempt
    trackFormSubmission('contact_form', {
      subject: data.subject,
      has_phone: !!data.phone,
      message_length: data.message?.length || 0
    });
    
    try {
      await addDocument('requests', {
        type: 'contact' as const,
        data: data as unknown as Record<string, unknown>,
        status: 'new' as const,
        notes: '',
      });

      notifyRequest('contact', data as unknown as Record<string, unknown>);
      toast.success(t('contact.success.title'));
      setIsSubmitted(true);
      reset();

      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);

    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
              {t('contact.subtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto mt-8 rounded-full" />
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                {t('contact.contactInfo.title')}
              </h2>
              
              <div className="space-y-8">
                {contactInfo.map((info, index) => {
                  const IconComponent = info.icon;
                  
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-4"
                    >
                      <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {info.title}
                        </h3>
                        <div className="space-y-1">
                          {info.details.map((detail, detailIndex) => (
                            <p key={detailIndex} className="text-gray-600">
                              {detail}
                            </p>
                          ))}
                        </div>
                        {info.link && (
                          <motion.a
                            href={info.link}
                            target={info.link.startsWith('http') ? '_blank' : undefined}
                            rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                            whileHover={{ scale: 1.05 }}
                            className="inline-block mt-2 text-red-600 hover:text-red-700 font-medium"
                          >
                            {info.linkText}
                          </motion.a>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Additional Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="mt-12 p-6 bg-gray-50 rounded-2xl"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {t('contact.customProjects.title')}
                </h3>
                <p className="text-gray-600 mb-4">
                  {t('contact.customProjects.description')}
                </p>
                <motion.a
                  href="/custom-request"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  <span>{t('contact.customProjects.button')}</span>
                  <motion.div
                    animate={{ x: [0, 3, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    →
                  </motion.div>
                </motion.a>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl p-8"
            >
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('contact.form.title')}</h2>
              </div>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 0.6 }}
                    className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </motion.div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {t('contact.success.title')}
                  </h3>
                  <p className="text-gray-600">
                    {t('contact.success.description')}
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.fullName')} *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        {...register('name')}
                        className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                          errors.name ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                        }`}
                        placeholder={t('contact.form.fullNamePlaceholder')}
                      />
                    </div>
                    {errors.name && (
                      <p className="mt-2 text-sm text-red-600">{t('contact.validation.nameRequired')}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.form.email')} *
                      </label>
                      <input
                        type="email"
                        {...register('email')}
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                          errors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                        }`}
                        placeholder={t('contact.form.emailPlaceholder')}
                      />
                      {errors.email && (
                        <p className="mt-2 text-sm text-red-600">
                          {errors.email.type === 'required' 
                            ? t('contact.validation.emailRequired')
                            : t('contact.validation.emailInvalid')
                          }
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.form.phone')}
                      </label>
                      <input
                        type="tel"
                        {...register('phone')}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none transition-colors"
                        placeholder={t('contact.form.phonePlaceholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.subject')} *
                    </label>
                    <select
                      {...register('subject')}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        errors.subject ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                      }`}
                    >
                      <option value="">{t('contact.form.selectSubject')}</option>
                      <option value="services">{t('contact.form.subjects.services')}</option>
                      <option value="custom">{t('contact.form.subjects.custom')}</option>
                      <option value="quote">{t('contact.form.subjects.quote')}</option>
                      <option value="appointment">{t('contact.form.subjects.appointment')}</option>
                      <option value="info">{t('contact.form.subjects.info')}</option>
                      <option value="other">{t('contact.form.subjects.other')}</option>
                    </select>
                    {errors.subject && (
                      <p className="mt-2 text-sm text-red-600">{t('contact.validation.subjectRequired')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.message')} *
                    </label>
                    <textarea
                      {...register('message')}
                      rows={5}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors resize-none ${
                        errors.message ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                      }`}
                      placeholder={t('contact.form.messagePlaceholder')}
                    />
                    {errors.message && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.message.type === 'required' 
                          ? t('contact.validation.messageRequired')
                          : t('contact.validation.messageTooShort')
                        }
                      </p>
                    )}
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={!isSubmitting ? { scale: 1.02, y: -2 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    className={`w-full flex items-center justify-center space-x-3 py-4 font-semibold text-lg rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl ${
                      isSubmitting
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full"
                        />
                        <span>{t('contact.form.submitting')}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>{t('contact.form.submit')}</span>
                      </>
                    )}
                  </motion.button>
                </form>
              )}
            </motion.div>
          </div>

          {/* Map Section */}
          <div className="mt-16">
            <FreeMap />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;